<template>
  <div id="diagnosis-form-dataset">
    <h3 class="grey--text text--darken-3 mt-10 d-flex">
      <span>Diagnosis <small><i>(Click a record to update)</i></small><span class="red--text">*</span></span>
        <base-popup-btn
          v-if="consultation.status != 'submitted'"
          class="ml-auto"
          @click="addDiagnosis"
        >
          Add Diagnosis
        </base-popup-btn>

    </h3>

    <p v-if="!diagnoses.length" class="error-text">This field is required.</p>

    <v-row align="center">
      <v-col cols="12">
        <base-table
          :headers="headers"
          :items="diagnoses"
        >
          <template v-slot:item="{ item }">
            <tr :id="'diagnosis-instance-' + item.diagnosis_index" @click="updateDiagnosis(item.diagnosis_index)">
              <td>{{ item.code }}</td>
              <td>{{ item.description }}</td>
              <td>{{ item.impression }}</td>
              <td>{{ item.created_on }}</td>
              <td class="text-capitalize">{{ item.record_type }}</td>
            </tr>
          </template>
        </base-table>

        <div style="width: 100%; text-align: center; color: red; padding: 15px;" v-show="elementVisible" class="error-message"><h3>{{ errorMessage }}</h3></div>

      </v-col>
    </v-row>

    <!-- DIAGNOSIS DIALOG -->
    <v-dialog
     v-model="diagnosisDialog"
     content-class="elevation-0"
     width="70vw"
     max-width="600px" 
    >
      <v-card>
        <v-card-title class="primary--text">Diagnosis</v-card-title>
        
        <v-card-text>
          <v-form ref="form" v-model="valid"> 
            <!-- code -->
            <v-autocomplete
              ref="diagnosisAutoComplete"
              :loading="isLoading"
              placeholder=" "
              :items="codes"
              item-value="medical_code"
              item-text="medical_code"
              v-model="code"
              clearable
              :search-input.sync="filter"
              :filter="filterObject"
              hide-no-data
              hide-selected
              @keypress="isSearching = true"
              @keydown="isSearching = false"
              @keypress.13.prevent
              @change="addToMostUsed"
              :rules="rules.required"
              :menu-props="{ maxHeight: 450 }"
              @click:clear="() => {this.isSearching=false; this.filter = ''; this.code = ''}"
              v-on:focus="() => {this.$vtt.startSpeechListener((text) => { this.setCode(text); }, 'diagnosis_code')}"
              v-on:click="() => {this.$vtt.setModel('diagnosis_code');}"
            >
              <template v-slot:label>
                <h3>Code <span class="red--text">*</span></h3>
              </template>

              <template v-slot:append>
                <v-icon>mdi-magnify</v-icon>
              </template>

              <!-- ##################################### favorites panel ##################################### -->

              <template v-if="!isSearching" v-slot:prepend-item>
                <favorites-panel>
                  <div v-if="favoriteCodes.length > 0">
                    <v-data-table
                        :items="favoriteCodes"
                        class="sticky mt-5 v-table-max-width"
                        :disable-sort="true"
                      >
                      <template v-slot:item="{ key, item }">
                        <tr>
                          <!-- Row Name -->
                          <td>
                            <diagnosis-item
                              :item="item"
                              :key="key"
                              panel-view
                              @click="setSelected(item, 'fav')"
                            />
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </div>

                  <div v-if="favoriteCodes.length == 0" class="text-center pt-5 mt-5">
                    <span class="pt-5 mt-5">No favorite ICD code added yet.</span>
                  </div>
                </favorites-panel>

                <!-- <hr style="border-top: 1px solid #cccccc;" /> -->

                <!-- ##################################### favorites panel ##################################### -->



                <!-- ##################################### most used panel ##################################### -->
                
                <!-- <most-used-panel>
                  <template v-for="(item, key) in mostUsedCodes">
                    <diagnosis-item
                      :item="item"
                      :key="key"
                      panel-view
                      @click="setSelected(item, 'used')"
                    >
                    </diagnosis-item>
                  </template>
                </most-used-panel> -->
              </template>

              <!-- ##################################### most used panel ##################################### -->

              <template v-slot:append-item>
                <hr style="border-top: 1px solid #cccccc;">

                <div class="d-flex mt-3 mb-1">
                  <v-btn
                    small
                    depressed
                    rounded
                    color="primary"
                    class="mx-2 px-4 text-none"
                    @click="togglePage('prev')"
                    :disabled="page==1"
                  >
                    Prev
                  </v-btn>

                  <v-spacer style="text-align: center;">Page {{page}} / {{ last_page }}</v-spacer>
                  
                  <v-btn
                    small
                    depressed
                    rounded
                    color="primary"
                    class="mx-2 px-4 text-none"
                    @click="togglePage('next')"
                    :disabled="last_page==page"
                  >
                    Next
                  </v-btn>
                </div>
              </template>

              <template v-slot:item="data">
                <!-- medicine item -->
                <diagnosis-item
                  :item="data.item"
                  :maxWidth="'500px;'"
                >
                </diagnosis-item>
              </template>
            </v-autocomplete>

            <!-- description -->
            <v-textarea
              rows="3"
              v-model="description"
              placeholder=" "
              v-on:focus="() => {this.$vtt.startSpeechListener((text) => { this.description = `${this.description || ''} ${text}`;}, 'diagnosis_desc')}"
              v-on:click="() => {this.$vtt.setModel('diagnosis_desc');}"
              @input="setHint(description, (replacement) => { description = replacement })"
              @keydown.tab="(e) => {
                if (!getReplacement(description)) { return true; } else { e.preventDefault(); description = getReplacement(description); clearReplacement();  }
              }"
            >
              <template v-slot:label>
                <h3>Description</h3>
              </template>
            </v-textarea>

            <!-- impression -->
            <v-textarea 
              placeholder=" " 
              v-model="impression" 
              :rules="rules.required" rows="3"
              v-on:focus="() => {this.$vtt.startSpeechListener((text) => { this.impression = `${this.impression || ''} ${text}`;}, 'diagnosis_impression')}"
              v-on:click="() => {this.$vtt.setModel('diagnosis_impression');}"
              >
              <template v-slot:label>
                <h3>Impression <span class="red--text">*</span></h3>
              </template>
            </v-textarea>

            <center>
              <h2 v-show="impression.length > 1000" style="color: red;">You have reached the maximum limit of 1000 characters</h2>
            </center>

            <VTTButton :mode="'checkbox'" />

          </v-form>
        </v-card-text>
        
        <!-- Actions -->
        <v-card-actions class="d-flex justify-center">
          <!-- Add/ Update Button -->
          <v-btn
            rounded
            small
            color="primary"
            class="px-6 mx-2"
            @click="saveDiagnosis"
          >
            {{ diagnosisUpdate?'Update':'Add' }}
          </v-btn>

          <!-- Remove Button -->
          <v-btn
            rounded
            small
            color="error"
            class="px-6 mx-2"
            v-if="diagnosisUpdate"
            @click="removeDiagnosis"
          >Remove</v-btn>

          <!-- Cancel Button -->
          <v-btn
            rounded
            small
            color="primary lighten-5"
            class="px-6 mx-2 primary--text"
            @click="cancelDiagnosis"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <hr />

  </div>
</template>

<script>

  import VTTButton from '../../../../../components/custom/VTTButton.vue';
  import textreplacementmixin from '../../../../../mixins/textreplacementmixin';

  export default {
    name: 'FormDiagnosis',

    mixins: [textreplacementmixin],

    components: {
      DiagnosisItem: () => import('./DiagnosisItem.vue'),
      VTTButton,
      // MostUsedPanel: () => import('./MostUsedPanel.vue'),
      FavoritesPanel: () => import('./FavoritesPanel.vue')
    },

    data: ()=>({
      errorMessage: "You can only create one diagnosis for this case",
      elementVisible: false,
      valid: false,
      rules: {
       required: [value => !!value || "This field is required."]
      },
      filter: '',
      page: 1,
      isSearching: false,
      isLoading: false,
      diagnosisDialog: false,
      diagnosisUpdate: false,
      headers: [
        { text: 'Medical Code', value: 'code', class:'primary--text', width: '100px' },
        { text: 'Short Description', value: 'description', class:'primary--text d-flex align-start' },
        { text: 'Impression', value: 'impression', class:'primary--text' },
        { text: 'Create On', value: 'created_on', class:'primary--text', width: '170px' },
        { text: 'Record Type', value: 'level', class:'primary--text', width: '150px' },
      ],
    }),

    /* ---------------------------------------------------------------- FUNCTION SEPARATOR ---------------------------------------------------------------- */

    watch: {
      diagnoses: function (val) {
        this.diagnoses = val;
      },
      async filter(val, old) {
        console.log('Filter:', {new: val, old: old});
        if ( (val == null && old == undefined) || val != '' ) {
          try {
            this.isLoading = true
            await this.$store.dispatch('diagnosis/searchCode', val);
            this.isLoading = false
            this.page = 1;
          } catch(e) {
            console.log('[error]', e)
            this.isLoading = false
          }
        } else if (val == null) {
          return;
        }
      }
    },

    /* ---------------------------------------------------------------- FUNCTION SEPARATOR ---------------------------------------------------------------- */

    computed: {
      last_page() { return this.$store.getters['diagnosis/last_page'] },
      recommendation_record_type() { return this.$store.getters['recommendation/record_type'] },
      consultation() { return this.$store.getters['consultation/activeInfo'] },

      codes() {
        return this.$store.getters['diagnosis/codes']
      },
      
      favoriteCodes() {
        return this.$store.getters['diagnosis/fave_codes']
      },
      
      mostUsedCodes() {
        return this.$store.getters['diagnosis/used_codes']
      },
      
      diagnoses: {
        get() { return this.$store.getters['diagnosis/diagnoses'] },
        set() { this.$store.getters['diagnosis/diagnoses'] }
      },

      code: {
        get() { return this.$store.getters['diagnosis/code'] },
        set(val) { this.$store.commit('diagnosis/updateCode', val) }
      },
      
      description: {
        get() { return this.$store.getters['diagnosis/description'] },
        set(val) { this.$store.commit('diagnosis/updateDescription', val) }
      },
      
      impression: {
        get() { return this.$store.getters['diagnosis/impression'] },
        set(val) { this.$store.commit('diagnosis/updateImpression', val) }
      },
      
      level: {
        get() { return this.$store.getters['diagnosis/level'] },
        set(val) { this.$store.commit('diagnosis/updateLevel', val) }
      },
    },

    /* ---------------------------------------------------------------- FUNCTION SEPARATOR ---------------------------------------------------------------- */

    async mounted() {
      await this.$store.dispatch('diagnosis/fetchCodes', {per_page: 10, page: this.page});
    },

    /* ---------------------------------------------------------------- FUNCTION SEPARATOR ---------------------------------------------------------------- */

    methods: {

      setSelected(item, origin) {
        this.$refs.diagnosisAutoComplete.focus();
        this.code = item.medical_code;
        this.filter = item.medical_code;
        this.autoFillDetails(origin);
        this.$refs.diagnosisAutoComplete.blur();
      },

      setCode (val) {
        let string = val.trim().replace('.', '');
        this.filter = string;
        console.log(string);
      },

      addDiagnosis() {

        let current_created_diagnosis = this.diagnoses.filter(item_instance => { if (item_instance.record_type == 'current') return item_instance}).length;
        if(current_created_diagnosis > 0) {
          this.elementVisible = true;
          setTimeout(() => this.elementVisible = false, 5000);
          return false;
        }

        this.$store.commit('diagnosis/clear');
        this.diagnosisDialog = true;
        this.diagnosisUpdate = false;
        this.isSearching = false;
      },

      updateDiagnosis(index) {
        this.diagnosisDialog = true;
        this.diagnosisUpdate = true;
        this.$store.commit('diagnosis/setActive', index);
      },

      cancelDiagnosis() {
        this.$refs.form.resetValidation();
        this.diagnosisDialog = false;
        this.diagnosisUpdate = false;
      },

      saveDiagnosis() {
        this.$refs.form.validate();

        if(this.impression.length > 1000) {
          return false;
        }

        if (this.valid) {
          if (this.diagnosisUpdate) {
            var index = this.$store.getters['diagnosis/active'];
            this.$store.commit('diagnosis/edit', index);
          }
          else
            this.$store.commit('diagnosis/add');

          this.$store.commit('diagnosis/clear');
          this.diagnosisDialog = false;
        }
      },

      removeDiagnosis() {
        this.$store.commit('diagnosis/remove');
        this.diagnosisDialog = false;
      },

      async togglePage(type) {

        let params = {};

        if(this.filter != null && this.filter.length > 0) {
          params = {per_page: 10, page: this.page, search: this.filter};
        } else {
          params = {per_page: 10, page: this.page};
        }

        if (type=='next') {
          if(this.last_page > this.page) {
            this.page++
            params.page++;
            await this.$store.dispatch('diagnosis/fetchCodes', params);
          }
        } else {
          this.page--;
          params.page--;
          await this.$store.dispatch('diagnosis/fetchCodes', params);
        }
        await this.$store.dispatch("diagnosis/getFavoritesMedicalCodes");
      },

      filterObject(item, queryText) {
        return (
          item.description.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1 ||
          item.medical_code.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
        );
      },

      autoFillDetails(origin = '') {
        let index;
        if (this.code) {
          switch (origin) {
            case 'fav':
              index = this.favoriteCodes.findIndex(item => item.medical_code == this.code)
              this.description = index >= 0 ? this.favoriteCodes[index].description : ''
              break;
            
            case 'used':
              index = this.mostUsedCodes.findIndex(item => item.medical_code == this.code)
              this.description = index >= 0 ? this.mostUsedCodes[index].description : ''
              break;
          
            default:
              index = this.codes.findIndex(item => item.medical_code == this.code)
              this.description = index >= 0 ? this.codes[index].description : ''
              break;
          }
        }
        else {
          this.description = ''
        }
      },

      // medication search
      addToMostUsed() {
        this.autoFillDetails()

        console.log('Codessssss: ', this.code)

        let index = this.codes.findIndex(
          item => item.medical_code == this.code
        )

        console.log('disgnoses indexxxx: ', index)

        if (index >= 0){
          // this.$store.commit('diagnosis/addToMostUsed', this.codes[index])
        }
          
      }
    }
  }
</script>

<style lang="scss" scoped>
  .capitalize { text-transform: capitalize; }
  .v-table-max-width { max-width: 478px; }
  .v-menu__content { max-height: 570px !important; }
</style>